/**
 * Utilitários para a tela pública de palpites do mata-mata da Copa 2026.
 * Lê as fases publicadas pelo organizador (Apps Script v6.3) e cruza com
 * o calendário oficial FIFA para saber quais jogos estão liberados.
 */

import {
  FASES_MATA_MATA,
  JOGOS_MATA_MATA,
  inicioJogoMataMataMs,
  type FaseMataMata,
  type JogoMataMata,
} from '../data/mataMataCopa2026';
import { nomeTime } from '../data/copaMundo2026';
import { resolverTimeIdParaBandeira } from './copaBandeiras';

const IDS_JOGOS_MM = new Set(JOGOS_MATA_MATA.map((j) => j.id));

export type PlacarParMM = { mandante: number; visitante: number };

/** Normaliza palpites vindos do GET listar (mapa, array legado ou célula mal parseada). */
export function normalizarPalpitesPlanilhaMM(bruto: unknown): Record<string, PlacarParMM> {
  const out: Record<string, PlacarParMM> = {};
  if (!bruto) return out;

  const gravar = (jogoId: string, mandante: unknown, visitante: unknown) => {
    const m = Number(mandante);
    const v = Number(visitante);
    if (!Number.isFinite(m) || !Number.isFinite(v) || m < 0 || v < 0) return;
    out[jogoId] = { mandante: m, visitante: v };
  };

  if (Array.isArray(bruto)) {
    bruto.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      const p = item as { jogoId?: string; mandante?: unknown; visitante?: unknown };
      if (p.jogoId) gravar(String(p.jogoId), p.mandante, p.visitante);
    });
    return out;
  }

  if (typeof bruto === 'object') {
    Object.keys(bruto as object).forEach((k) => {
      if (k.startsWith('__')) return;
      const p = (bruto as Record<string, unknown>)[k];
      if (!p || typeof p !== 'object') return;
      const placar = p as { mandante?: unknown; visitante?: unknown; jogoId?: string };
      gravar(placar.jogoId ? String(placar.jogoId) : k, placar.mandante, placar.visitante);
    });
  }
  return out;
}

/** Junta linhas fase=matamata e palpites MM que por engano ficaram na linha de grupos. */
export function coletarParticipantesMatamata(
  linhas: Array<{
    nome: string;
    setor?: string;
    fase?: string;
    enviadoEm?: string;
    excluidoEm?: string;
    palpites?: unknown;
  }>
): Array<{
  nome: string;
  setor: string;
  enviadoEm: string;
  palpites: Record<string, PlacarParMM>;
}> {
  const mapa = new Map<
    string,
    { nome: string; setor: string; enviadoEm: string; palpites: Record<string, PlacarParMM> }
  >();

  linhas.forEach((p) => {
    if (p.excluidoEm) return;
    const palpitesNorm = normalizarPalpitesPlanilhaMM(p.palpites);
    const somenteMM: Record<string, PlacarParMM> = {};
    Object.keys(palpitesNorm).forEach((jid) => {
      if (!IDS_JOGOS_MM.has(jid)) return;
      somenteMM[jid] = palpitesNorm[jid];
    });
    if (Object.keys(somenteMM).length === 0) return;

    const chave = `${p.nome.trim().toLowerCase()}|${(p.setor || '').trim().toLowerCase()}`;
    const prev = mapa.get(chave);
    if (prev) {
      mapa.set(chave, {
        ...prev,
        palpites: { ...prev.palpites, ...somenteMM },
        enviadoEm: p.enviadoEm || prev.enviadoEm,
      });
    } else {
      mapa.set(chave, {
        nome: p.nome,
        setor: p.setor || '',
        enviadoEm: p.enviadoEm || '',
        palpites: somenteMM,
      });
    }
  });

  return [...mapa.values()];
}

/** Atualiza cache de fases publicadas sem apagar dados bons quando o servidor vem vazio. */
export function mesclarFasesPublicadasServidor(fasesServidor: FasePublicada[]): FasePublicada[] {
  const norm = normalizarFasesPublicadas(fasesServidor);
  if (norm.length > 0) return norm;
  return lerFasesPublicadasCache();
}

/** Lado de um jogo do mata-mata como vem do servidor (após organizador publicar). */
export interface LadoPublicadoMM {
  /** Identificador da seleção (ex.: 'BRA'). Vazio se ainda for placeholder. */
  timeId: string;
  /** Nome a mostrar (ex.: 'Brasil', 'Vencedor 89', '3º A/B/C/D/F'). */
  rotulo: string;
}

/** Estrutura de cada jogo dentro de uma fase publicada. */
export interface JogoFasePublicada {
  id: string;
  ladoA: LadoPublicadoMM;
  ladoB: LadoPublicadoMM;
}

/** Estrutura como vem do GET acao=jogosFaseMM do Apps Script. */
export interface FasePublicada {
  fase: FaseMataMata;
  publicadoEm: string;
  jogos: JogoFasePublicada[];
}

/** Resposta completa do GET acao=jogosFaseMM. */
export interface RespostaJogosFaseMM {
  ok: boolean;
  fases: FasePublicada[];
  agoraMs?: number;
  erro?: string;
}

/** Palpite de outro participante em um jogo específico do mata-mata. */
export interface PalpiteOutroJogoMM {
  nome: string;
  setor: string;
  mandante: number;
  visitante: number;
  enviadoEm: string;
  ehVoce: boolean;
}

/** Monta mapa jogoId → palpites de todos (para exibir só após início do jogo). */
export function montarMapaPalpitesOutrosMM(
  linhasMatamata: Array<{
    nome: string;
    setor?: string;
    enviadoEm?: string;
    palpites: Record<string, { mandante: number; visitante: number }> | unknown;
  }>,
  meuNome: string,
  meuSetor: string
): Record<string, PalpiteOutroJogoMM[]> {
  const meuNomeNorm = meuNome.trim().toLowerCase();
  const meuSetorNorm = meuSetor.trim().toLowerCase();
  const mapaOutros: Record<string, PalpiteOutroJogoMM[]> = {};
  linhasMatamata.forEach((p) => {
    const palpites = normalizarPalpitesPlanilhaMM(p.palpites);
    if (Object.keys(palpites).length === 0) return;
    Object.keys(palpites).forEach((jid) => {
      if (jid.indexOf('__') === 0) return;
      if (!IDS_JOGOS_MM.has(jid)) return;
      const placar = palpites[jid];
      if (!placar) return;
      if (!mapaOutros[jid]) mapaOutros[jid] = [];
      const ehVoce =
        p.nome.trim().toLowerCase() === meuNomeNorm &&
        (p.setor || '').trim().toLowerCase() === meuSetorNorm;
      mapaOutros[jid].push({
        nome: p.nome,
        setor: p.setor || '',
        mandante: placar.mandante,
        visitante: placar.visitante,
        enviadoEm: p.enviadoEm || '',
        ehVoce,
      });
    });
  });
  Object.keys(mapaOutros).forEach((jid) => {
    mapaOutros[jid].sort((a, b) => {
      if (a.ehVoce !== b.ehVoce) return a.ehVoce ? -1 : 1;
      return a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' });
    });
  });
  return mapaOutros;
}

const STORAGE_KEY_FASES_MM = 'copa-mm-fases-publicadas-v1';

/** Normaliza lado publicado (timeId maiúsculo; fallback pelo rótulo). */
export function normalizarLadoPublicadoMM(
  lado: Partial<LadoPublicadoMM> & Record<string, unknown> | undefined
): LadoPublicadoMM {
  const brutoId = String(lado?.timeId ?? lado?.time_id ?? '').trim();
  const rotulo = String(lado?.rotulo ?? lado?.nome ?? '').trim();
  const idResolvido = resolverTimeIdParaBandeira(brutoId, rotulo);
  return {
    timeId: idResolvido,
    rotulo: rotulo || (idResolvido ? nomeTime(idResolvido) : 'TBD'),
  };
}

/** Normaliza fases vindas do Apps Script (cache ou GET). */
export function normalizarFasesPublicadas(fases: FasePublicada[]): FasePublicada[] {
  return (fases || []).map((fp) => ({
    fase: fp.fase,
    publicadoEm: fp.publicadoEm || '',
    jogos: (fp.jogos || []).map((j) => ({
      id: j.id,
      ladoA: normalizarLadoPublicadoMM(j.ladoA as Partial<LadoPublicadoMM>),
      ladoB: normalizarLadoPublicadoMM(j.ladoB as Partial<LadoPublicadoMM>),
    })),
  }));
}

/** Lê o cache local das fases publicadas. */
export function lerFasesPublicadasCache(): FasePublicada[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_FASES_MM);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return normalizarFasesPublicadas(Array.isArray(arr) ? (arr as FasePublicada[]) : []);
  } catch {
    return [];
  }
}

/** Salva o cache local das fases publicadas. */
export function salvarFasesPublicadasCache(fases: FasePublicada[]): void {
  try {
    localStorage.setItem(STORAGE_KEY_FASES_MM, JSON.stringify(fases));
  } catch {
    /* quota */
  }
}

/** Busca o jogo (estrutura oficial) por id. */
export function jogoOficialPorId(id: string): JogoMataMata | undefined {
  return JOGOS_MATA_MATA.find((j) => j.id === id);
}

/** True se o jogo individual já começou (palpite encerrado). */
export function jogoTravado(id: string, agoraMs: number = Date.now()): boolean {
  return agoraMs >= inicioJogoMataMataMs(id);
}

/** Formata uma data ISO (com offset BRT -03:00) em "DD/MM HH:MM" ou "DD/MM/AAAA HH:MM". */
export function formatarDataHoraIsoBrt(iso: string, opts?: { comAno?: boolean }): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '';
  // Subtrai 3h pra ler como UTC-3 (BRT) usando getUTC*
  const dBrt = new Date(ms - 3 * 60 * 60 * 1000);
  const dia = String(dBrt.getUTCDate()).padStart(2, '0');
  const mes = String(dBrt.getUTCMonth() + 1).padStart(2, '0');
  const hora = String(dBrt.getUTCHours()).padStart(2, '0');
  const min = String(dBrt.getUTCMinutes()).padStart(2, '0');
  if (opts?.comAno) {
    const ano = dBrt.getUTCFullYear();
    return `${dia}/${mes}/${ano} ${hora}:${min}`;
  }
  return `${dia}/${mes} ${hora}:${min}`;
}

/** Formata "DD/MM HH:MM" em horário de Brasília a partir do id do jogo. */
export function formatarDataHoraJogo(id: string): string {
  const jogo = jogoOficialPorId(id);
  if (!jogo) return '';
  return formatarDataHoraIsoBrt(jogo.dataHoraBrt);
}

/** Junta dados oficiais + dados publicados do servidor para a UI. */
export interface JogoMataMataParaUi {
  id: string;
  fase: FaseMataMata;
  numeroFifa: number;
  ordem: number;
  cidade: string;
  estadio: string;
  dataHoraBrt: string;
  ladoA: LadoPublicadoMM;
  ladoB: LadoPublicadoMM;
  travado: boolean;
  /** Instante (ms epoch) em que o jogo começa = palpite trava. */
  inicioMs: number;
}

/** True se o organizador publicou este jogo com os dois times definidos. */
export function jogoMMPublicadoComTimesConfirmados(j: JogoFasePublicada): boolean {
  return Boolean(
    j?.ladoA?.timeId?.trim() && j?.ladoB?.timeId?.trim()
  );
}

/** Só jogos que o organizador publicou no link (ambos os times confirmados). */
export function jogosLiberadosParaPalpite(
  fasesPublicadas: FasePublicada[],
  agoraMs: number = Date.now()
): JogoMataMataParaUi[] {
  const out: JogoMataMataParaUi[] = [];
  fasesPublicadas.forEach((fp) => {
    (fp.jogos || []).forEach((jp) => {
      if (!jogoMMPublicadoComTimesConfirmados(jp)) return;
      const oficial = jogoOficialPorId(jp.id);
      if (!oficial) return;
      out.push({
        id: jp.id,
        fase: oficial.fase,
        numeroFifa: oficial.numeroFifa,
        ordem: oficial.ordem,
        cidade: oficial.cidade,
        estadio: oficial.estadio,
        dataHoraBrt: oficial.dataHoraBrt,
        ladoA: jp.ladoA,
        ladoB: jp.ladoB,
        travado: agoraMs >= inicioJogoMataMataMs(jp.id),
        inicioMs: inicioJogoMataMataMs(jp.id),
      });
    });
  });
  return out.sort((a, b) => a.inicioMs - b.inicioMs || a.numeroFifa - b.numeroFifa);
}

/** Mescla a info oficial do jogo com a publicada (se houver). Uso interno/legado. */
export function jogosParaUi(
  fasesPublicadas: FasePublicada[],
  agoraMs: number = Date.now()
): JogoMataMataParaUi[] {
  const mapaPub = new Map<string, JogoFasePublicada>();
  fasesPublicadas.forEach((fp) =>
    fp.jogos.forEach((jp) => mapaPub.set(jp.id, jp))
  );
  return JOGOS_MATA_MATA.map((j) => {
    const pub = mapaPub.get(j.id);
    return {
      id: j.id,
      fase: j.fase,
      numeroFifa: j.numeroFifa,
      ordem: j.ordem,
      cidade: j.cidade,
      estadio: j.estadio,
      dataHoraBrt: j.dataHoraBrt,
      ladoA: pub?.ladoA || { timeId: '', rotulo: rotuloPlaceholderLado(j, 'A') },
      ladoB: pub?.ladoB || { timeId: '', rotulo: rotuloPlaceholderLado(j, 'B') },
      travado: agoraMs >= inicioJogoMataMataMs(j.id),
      inicioMs: inicioJogoMataMataMs(j.id),
    };
  });
}

/** Rótulo padrão (se a fase ainda não foi publicada). */
function rotuloPlaceholderLado(jogo: JogoMataMata, qual: 'A' | 'B'): string {
  const lado = qual === 'A' ? jogo.ladoA : jogo.ladoB;
  if (lado.tipo === 'pos-grupo') return `${lado.posicao}º Grupo ${lado.grupo}`;
  if (lado.tipo === 'melhor-3o')
    return `3º (${lado.gruposElegiveis.join('/')})`;
  if (lado.tipo === 'vencedor') {
    const orig = JOGOS_MATA_MATA.find((j) => j.id === lado.jogoId);
    return orig ? `Vencedor jogo ${orig.numeroFifa}` : 'Vencedor TBD';
  }
  if (lado.tipo === 'perdedor') {
    const orig = JOGOS_MATA_MATA.find((j) => j.id === lado.jogoId);
    return orig ? `Perdedor jogo ${orig.numeroFifa}` : 'Perdedor TBD';
  }
  return 'TBD';
}

/** Filtra os jogos por fase, mantendo a ordem natural. */
export function jogosUiPorFase(
  jogos: JogoMataMataParaUi[],
  fase: FaseMataMata
): JogoMataMataParaUi[] {
  return jogos
    .filter((j) => j.fase === fase)
    .sort((a, b) => a.inicioMs - b.inicioMs || a.numeroFifa - b.numeroFifa);
}

/** Fases com ao menos um jogo publicado e com os dois times confirmados. */
export function fasesComMatchupsPublicados(
  fasesPublicadas: FasePublicada[]
): FaseMataMata[] {
  const comJogo = new Set<FaseMataMata>();
  fasesPublicadas.forEach((fp) => {
    if ((fp.jogos || []).some(jogoMMPublicadoComTimesConfirmados)) {
      comJogo.add(fp.fase);
    }
  });
  return FASES_MATA_MATA.filter((f) => comJogo.has(f));
}
